import React, { useEffect, useState } from 'react'
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Select,
  TimePicker,
  Switch,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import axiosInstance from '../../Utils/axiosInstance'

import {
  CreateSlotData,
  DeleteSlotData,
  GetSlotData,
  UpdateSlotData,
} from "../../API's/AdminApi"

const { Option } = Select

export default function SlotMaster() {
  const [data, setData] = useState([])
  const [searchText, setSearchText] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editData, setEditData] = useState(null)
  const [deleteId, setDeleteId] = useState(null)

  const [tableLoading, setTableLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [pagination, setPagination] = useState({
  current: 1,
  pageSize: 5,
})

  const [form] = Form.useForm()

  // ---------------- GET DATA ----------------
  const getSlots = async () => {
    try {
      setTableLoading(true)

      const res = await axiosInstance.get(GetSlotData)

      setData(res.data || [])
    } catch (error) {
      toast.error('Failed to load slots')
    } finally {
      setTableLoading(false)
    }
  }

  useEffect(() => {
    getSlots()
  }, [])

  // ---------------- ADD / UPDATE ----------------
  const onFinish = async (values) => {
    try {
      setSubmitLoading(true)

      const payload = {
        id: editData?.id,
        days: values.day.join(','),
        timeFrom: values.time[0].format('HH:mm:ss'), //24 hrs format
        timeTo: values.time[1].format('HH:mm:ss'),
        gender: parseInt(values.gender),
        intake: parseInt(values.intakeLimit),
        status: values.status ? 1 : 0,
      }

      let res

      if (editData) {
        res = await axiosInstance.put(UpdateSlotData, payload)

        if (res.data === 'UPDATED') {
          toast.success('Slot updated successfully')
        }
      } else {
        res = await axiosInstance.post(CreateSlotData, payload)

        if (res.data === 'SAVED') {
          toast.success('Slot created successfully')
        }
      }

      form.resetFields()
      setShowForm(false)
      setEditData(null)
      getSlots()
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong')
    } finally {
      setSubmitLoading(false)
    }
  }

  // ---------------- EDIT ----------------
  const handleEdit = (record) => {
    setEditData(record)
    setShowForm(true)

    form.setFieldsValue({
      day: record.days?.split(','),
      gender: String(record.gender), // important fix
      intakeLimit: record.intake,
      time: [
        dayjs(record.timeFrom, 'HH:mm:ss'),
        dayjs(record.timeTo, 'HH:mm:ss'),
      ],
      status: record.status === 1,
    })
  }
  // ---------------- DELETE ----------------
  const confirmDelete = async () => {
    try {
      setDeleteLoading(true)

      await axiosInstance.delete(`${DeleteSlotData}?id=${deleteId}`)

      toast.success('Slot deleted successfully')

      setDeleteId(null)

      getSlots()
    } catch (error) {
      toast.error('Failed to delete slot')
    } finally {
      setDeleteLoading(false)
    }
  }

  // ---------------- TABLE COLUMNS ----------------

  const dayMap = {
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
    7: 'Sunday',
  }

  const genderMap = {
    1: 'Male',
    2: 'Female',
    3: 'Both',
  }

  const columns = [
    {
      title: 'Sr No',
      render: (text, record, index) => index + 1,
    },

    {
      title: 'Day',
      render: (_, record) =>
        record.days
          .split(',')
          .map((d) => dayMap[d])
          .join(', '),
    },

    {
      title: 'Time',
      render: (_, record) =>
        record?.timeFrom && record?.timeTo
          ? `${dayjs(record.timeFrom, 'HH:mm:ss').format('hh:mm A')} - 
         ${dayjs(record.timeTo, 'HH:mm:ss').format('hh:mm A')}`
          : '-',
    },

    {
      title: 'Gender',
      render: (_, record) => genderMap[record.gender] || '-',
    },

    {
      title: 'Intake Limit',
      dataIndex: 'intake',
    },

    {
      title: 'Action',
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ marginRight: 8 }}
          />

          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => setDeleteId(record.id)}
          />
        </>
      ),
    },
  ]

  // ---------------- SEARCH ----------------
  const filteredData = data.filter((item) =>
    item?.days?.toString().includes(searchText),
  )

  return (
    <div>
      {/* TABLE */}
      {!showForm && (
        <>
          <div className="page-background p-2 p-md-4">
            <div className="page-header">
              <h2>
                <ClockCircleOutlined style={{ marginRight: 8 }} />
                Slot Master
              </h2>
            </div>

            <Row gutter={16} className="pb-2 m-0">
              <Col xs={24} md={6} xl={15}></Col>

              <Col xs={24} md={12} xl={6} className="pt-2 pt-md-0">
                <Input
                  placeholder="Search here"
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </Col>

              <Col xs={24} md={6} xl={3} className="pt-2 pt-md-0 mt-1">
                <button
                  className="btn-aqua"
                  onClick={() => {
                    setShowForm(true)
                    setEditData(null)
                    form.resetFields()
                  }}
                >
                  <PlusOutlined /> Add Slot
                </button>
              </Col>
            </Row>

            <Table
              className="custom-table-header shadow"
              columns={columns}
              bordered
              dataSource={filteredData}
              rowKey="id"
              loading={tableLoading}
              style={{ overflowX: 'auto', backgroundColor: 'white' }}
              pagination={{
                showSizeChanger: true, // enable page size dropdown
                pageSizeOptions: ['5', '10', '20', '50'],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          </div>
        </>
      )}

      {/* FORM */}
      {showForm && (
        <div className="row p-0 m-0">
          <div className="col-12 col-md-3 col-lg-4"></div>
          <div className="col-12 col-md-6 col-lg-4 page-background1 p-2 p-md-4">
            {/* HEADER */}
            <div className="page-header">
              <h2>
                <ClockCircleOutlined style={{ marginRight: 8 }} />
                Slot Master
              </h2>
            </div>

            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Form.Item label="Day" name="day" rules={[{ required: true }]}>
                <Select mode="multiple" placeholder="Select Days">
                  <Option value="1">Monday</Option>
                  <Option value="2">Tuesday</Option>
                  <Option value="3">Wednesday</Option>
                  <Option value="4">Thursday</Option>
                  <Option value="5">Friday</Option>
                  <Option value="6">Saturday</Option>
                  <Option value="7">Sunday</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Slot Time"
                name="time"
                rules={[{ required: true }]}
              >
                <TimePicker.RangePicker
                  size="large"
                  format="hh:mm A"
                  use12Hours
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                label="Gender"
                name="gender"
                rules={[{ required: true }]}
              >
                <Select placeholder="Select Gender">
                  <Option value="1">Male</Option>
                  <Option value="2">Female</Option>
                  <Option value="3">Both</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Intake Limit"
                name="intakeLimit"
                rules={[{ required: true }]}
              >
                <Input type="number" placeholder="Enter limit" />
              </Form.Item>

              {/* <Form.Item label="Status" name="status" valuePropName="checked">
                <Switch
                  checkedChildren="Enable"
                  unCheckedChildren="Disable"
                  defaultChecked
                />
              </Form.Item> */}

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitLoading}
                >
                  {editData ? 'Update' : 'Submit'}
                </Button>

                <Button
                  style={{ marginLeft: 10 }}
                  onClick={() => {
                    setShowForm(false)
                    setEditData(null)
                    form.resetFields()
                    form.setFieldsValue({
                      status: true,
                    })
                  }}
                >
                  Cancel
                </Button>
              </Form.Item>
            </Form>
          </div>
          <div className="col-12 col-md-3 col-lg-4"></div>
        </div>
      )}

      {/* DELETE MODAL */}
      <Modal
        title="Confirm Delete"
        open={deleteId !== null}
        onOk={confirmDelete}
        confirmLoading={deleteLoading}
        onCancel={() => setDeleteId(null)}
      >
        Are you sure you want to delete this slot?
      </Modal>
    </div>
  )
}
